import { Injectable } from '@angular/core';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { ApolloError } from '@apollo/client/core';
import { Store } from '@ngxs/store';
import { AuthLogout } from '@x/ecommerce-shop/app/core/auth/state/auth.actions';
import { AuthState } from '@x/ecommerce-shop/app/core/auth/state/auth.state';
import { RootModuleErrorHandlerProvider } from '@x/ecommerce-shop/app/core/shop-error/shop-error-handler/shop-error-handler.service';
import { ShopSnackbarService } from '@x/ecommerce-shop/app/core/shop-snackbar/shop-snackbar.service';
import { firstValueFrom } from 'rxjs';

@Injectable()
export class AuthErrorHandlerService implements RootModuleErrorHandlerProvider {
  constructor(
    private store: Store,
    private router: Router,
    private shopSnackbarService: ShopSnackbarService,
    private route: ActivatedRoute,
  ) {}

  /*
   * Refresh tokens through the auth flow
   * 1) logout
   * 2) login / authenticate
   * 3) refresh user token
   * 4) refresh order token associated with user.
   *
   * */
  async invalidTokenHandler() {
    const loggedInUser = this.store.selectSnapshot(AuthState.loggedInUser)?.id;

    if (!loggedInUser) return;

    // logout
    await firstValueFrom(this.store.dispatch(new AuthLogout()));

    // show snackbar
    this.shopSnackbarService.general(
      {
        icon: 'sign-in',
        message: $localize`:@@Error.LoginSessionExpired:Your logged-in session has expired. Please login to continue your session.`,
      },
      {
        duration: 7000,
        verticalPosition: 'bottom',
        horizontalPosition: 'center',
      },
    );

    //navigate
    const queryParams: Params = {
      blockedRoute: this.router.url.split('?')[0],
      ...this.route.snapshot.queryParams,
    };

    return await this.router.navigate(['/login'], {
      queryParams,
    });
  }

  async handleApolloError(error: ApolloError): Promise<any> {
    if (error.message === 'auth.invalid_token') {
      return await this.invalidTokenHandler();
    }
  }
}
